/**
 * P3 Phase 3: Upgrade Modal with Stripe Checkout Integration
 * 
 * ARCHITECTURE:
 * - Uses StripeService to create checkout sessions
 * - Redirects to Stripe Checkout (no payment form in app)
 * - Enterprise plan shows "Contact Sales" flow
 * 
 * STATE RULES (NON-NEGOTIABLE):
 * - Frontend NEVER assumes payment success
 * - After Stripe redirect, frontend polls backend for subscription state
 * - UI changes ONLY after backend confirms subscription
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContent } from '../hooks/useContent';
import { usePackageCatalog } from '../context/PackageCatalogContext';
import { useClinicData } from '../context/ClinicDataContext';
import { useClinicScope } from '../hooks/useClinicScope';
import StripeService from '../services/StripeService';
import SubscriptionLegalGate from './SubscriptionLegalGate';
import { IyzicoCheckoutForm } from './subscription/IyzicoCheckoutForm';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  CreditCard, Lock, CheckCircle, ArrowRight, 
  Zap, Crown, Building2, Shield, X, Loader2,
  Phone, Mail, MessageSquare
} from 'lucide-react';
import { formatPrice, FEATURE_DEFINITIONS } from '../config/packages';
import { toast } from '../hooks/use-toast';

// Step enum
const STEPS = {
  CONFIRM: 'confirm',
  LEGAL_CONSENT: 'legal_consent',           // TREATX-LEGAL-PLATFORM-V1_4
  ENTERPRISE_CONTACT: 'enterprise_contact', // P3: Contact Sales step
  REDIRECTING: 'redirecting',               // P3: Redirecting to Stripe
  PSP_FORM: 'psp_form',                     // P0-F3: embedded provider form
  ENTERPRISE_SUBMITTED: 'enterprise_submitted',
};

// Canonical PSP-neutral internal id generator. Never derived from a
// Stripe / Iyzico id. Used to key legal evidence on user_consents.
const _newAttemptId = () => {
  try {
    const c = window.crypto || window.msCrypto;
    if (c && c.randomUUID) return `sub_att_${c.randomUUID()}`;
  } catch (_e) { /* noop */ }
  return `sub_att_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const _tokenHeader = () => {
  const t = ''; // FIND-H-08: cookie handles auth
  return {};
};

const UpgradeModal = ({ 
  isOpen,
  onClose,
  selectedPackageId,
  currentPackageId,
  onUpgradeComplete,
}) => {
  const { t } = useTranslation();
  const { catalog, refreshCatalog } = usePackageCatalog();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();
  const [step, setStep] = useState(STEPS.CONFIRM);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Enterprise contact form state
  const [enterpriseMessage, setEnterpriseMessage] = useState('');

  // TREATX-LEGAL-PLATFORM-V1_4 — canonical subscription attempt id
  // and the last submission error so the FE never fabricates a Stripe
  // id to key legal evidence.
  const [subscriptionAttemptId, setSubscriptionAttemptId] = useState(() => _newAttemptId());
  const [legalSubmitting, setLegalSubmitting] = useState(false);
  // TREATX-IYZICO-P0F3-B — provider-returned embedded checkout markup.
  const [pspFormContent, setPspFormContent] = useState(null);
  const clinicCountry = clinicUser?.country || null;
  
  // Refresh catalog when modal opens
  React.useEffect(() => {
    if (isOpen && refreshCatalog) {
      refreshCatalog();
      setStep(STEPS.CONFIRM);
      setError(null);
      setEnterpriseMessage('');
      // Mint a fresh subscription_attempt_id every time the modal opens
      // so the FE never accidentally re-uses one across separate flows.
      setSubscriptionAttemptId(_newAttemptId());
      setLegalSubmitting(false);
      setPspFormContent(null);
    }
  }, [isOpen, refreshCatalog]);
  
  if (!isOpen || !selectedPackageId) return null;
  
  const selectedPackage = catalog[selectedPackageId];
  const currentPackage = catalog[currentPackageId];
  const isEnterprise = StripeService.isContactSalesRequired(selectedPackageId);
  
  if (!selectedPackage) {
    console.warn(`[UpgradeModal] Package '${selectedPackageId}' not found in catalog.`);
    return null;
  }
  
  const packageIcons = {
    STARTER: Zap,
    PRO: Crown,
    ENTERPRISE: Building2,
  };
  
  const PackageIcon = packageIcons[selectedPackageId] || Zap;
  
  /**
   * P3 Phase 3: Handle checkout with Stripe
   * Creates checkout session and redirects to Stripe
   */
  const handleStripeCheckout = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const clinicId = rbacClinicId || clinicUser?.clinicId;
      const clinicEmail = clinicUser?.email;
      const clinicName = clinicUser?.clinicName || 'Your Clinic';
      
      if (!clinicId) {
        setError('Clinic ID not found. Please re-login.');
        setIsLoading(false);
        return;
      }
      
      // Create checkout session via backend
      // P3 Phase 4: Include clinicName for email notifications
      const checkout = await StripeService.createCheckoutSession({
        packageId: selectedPackageId,
        clinicId,
        clinicEmail,
        clinicName,
      });

      // ZERO-PRICE-UPGRADE-01 — a published price of 0 is activated by the
      // canonical upgrade endpoint itself, so there is no PSP session and no
      // checkout_url to follow. Success is accepted ONLY on the backend's
      // explicit confirmation; an absent URL on its own is an error, never an
      // activation. No redirect, and no payment-redirect state.
      if (checkout.activated === true && checkout.paymentRequired === false) {
        setIsLoading(false);
        setLegalSubmitting(false);
        setStep(STEPS.CONFIRM);
        if (onUpgradeComplete) {
          // Existing completion callback — also re-reads the canonical
          // subscription state and raises the localized success toast.
          await onUpgradeComplete(selectedPackageId, {
            activated: true,
            paymentRequired: false,
            amount: checkout.amount,
            currency: checkout.currency,
            sessionId: checkout.sessionId,
          });
        } else {
          toast({
            title: t('upgrade.activatedTitle', 'Plan Activated'),
            description: t('upgrade.activatedDesc', 'Your new plan is active. No payment was required.'),
          });
          onClose && onClose();
        }
        return;
      }

      // TREATX-IYZICO-P0F3-B — an EMBEDDED provider (iyzico) returns the
      // documented checkout-form markup instead of a hosted URL. Rendering it
      // is still NOT success: the provider returns the browser to the backend
      // callback, which verifies the collection before anything activates.
      if (checkout.formContent) {
        setPspFormContent({
          content: checkout.formContent,
          expiresIn: checkout.tokenExpiresIn,
          // P0-F6: the BACKEND says who is collecting; branding follows that,
          // never a frontend guess or the environment default.
          provider: checkout.provider,
        });
        setIsLoading(false);
        setStep(STEPS.PSP_FORM);
        return;
      }

      if (!checkout.checkoutUrl) {
        throw new Error(t('upgrade.checkoutUnavailable', 'Checkout could not be started. Please try again or contact TreatX support.'));
      }

      // Redirect to Stripe Checkout (paid upgrades — unchanged)
      // User will be redirected back to success/cancel URL after payment
      setStep(STEPS.REDIRECTING);
      window.location.href = checkout.checkoutUrl;
      
    } catch (err) {
      console.error('[UpgradeModal] Stripe checkout error:', err);
      // TREATX-IYZICO-P0F4-C — the payment provider requires statutory billing
      // details TreatX already stores. Name the missing CANONICAL fields so the
      // clinic can fix them, instead of a generic checkout failure. Field names
      // only: no identity value is ever shown or logged.
      const detail = err && err.response && err.response.data && err.response.data.detail;
      if (detail && detail.code === 'billing_profile_incomplete') {
        const fields = (detail.missing_fields || [])
          .map((f) => t(`upgrade.billingField.${f}`, f))
          .join(', ');
        setError(`${t('upgrade.billingIncomplete', 'Your billing details are incomplete, so payment cannot be started.')} ${fields}`.trim());
        setStep(STEPS.CONFIRM);
        setIsLoading(false);
        return;
      }
      setError(err.message || 'Failed to start checkout. Please try again.');
      setStep(STEPS.CONFIRM);
      setIsLoading(false);
      
      toast({
        title: t('upgrade.checkoutError', 'Checkout Error'),
        description: err.message,
        variant: 'destructive',
      });
    }
  };
  
  /**
   * TREATX-LEGAL-PLATFORM-V1_4 — pre-payment legal acceptance handler.
   * Fired by <SubscriptionLegalGate/> once every mandatory document is
   * checked. Submits the canonical PSP-neutral payload to
   * POST /api/subscriptions/clinics — the ONLY entry point that
   * persists legal evidence for the `subscription_purchase` journey.
   * Consent is keyed by the client-generated subscription_attempt_id,
   * never by a Stripe / Iyzico id.
   */
  const handleLegalAccept = async ({ consents, noneRequired = false }) => {
    setLegalSubmitting(true);
    setError(null);
    try {
      const clinicId = rbacClinicId || clinicUser?.clinicId;
      if (!clinicId) {
        setError('Clinic ID not found. Please re-login.');
        setLegalSubmitting(false);
        return;
      }

      // Canonical submission — no PSP-specific field anywhere.
      const body = {
        clinic_id: clinicId,
        plan_code: selectedPackageId,
        billing_cycle: 'monthly',
        subscription_attempt_id: subscriptionAttemptId,
        country_context: clinicCountry,
        consents: consents || [],
      };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/subscriptions/clinics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ..._tokenHeader() },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (res.status === 422) {
        // Hard block from the pre-action gate — surface it and keep the
        // user on the legal step so they can accept the missing docs.
        // The global error handler may wrap our HTTPException(detail=dict)
        // into {code:'VALIDATION_ERROR', message:'<repr>'} — accept both.
        const raw = data?.detail ?? {};
        let inner = typeof raw === 'object' ? raw : {};
        if (data?.code === 'VALIDATION_ERROR' && typeof data?.message === 'string') {
          try { inner = JSON.parse(data.message.replace(/'/g, '"')); } catch (_e) { /* keep {} */ }
        }
        const missing = inner?.missing || data?.missing || [];
        const detailMsg = inner?.message
          || data?.detail?.message
          || (typeof data?.message === 'string' && !data.message.startsWith('{') ? data.message : null)
          || 'Legal evidence required.';
        setError(`${detailMsg}${missing.length ? ` Missing: ${missing.join(', ')}` : ''}`);
        setLegalSubmitting(false);
        return;
      }
      if (!res.ok) {
        setError(data?.detail?.message || data?.message || data?.detail || 'Subscription creation failed.');
        setLegalSubmitting(false);
        return;
      }

      // Success — subscription is in PENDING_PAYMENT with consent stamped.
      // Now hand off to the existing Stripe checkout flow.
      if (noneRequired) {
        // No legal docs were required; nothing to log beyond audit.
      }
      await handleStripeCheckout();
    } catch (err) {
      console.error('[UpgradeModal] legal accept error:', err);
      setError(err?.message || 'Could not submit legal acceptance.');
      setLegalSubmitting(false);
    }
  };

  /**
   * P3 Phase 3: Handle upgrade flow
   * Routes to Stripe or Enterprise contact based on package.
   * TREATX-LEGAL-PLATFORM-V1_4: standard plans now enter the legal
   * consent step BEFORE any PSP session is opened.
   */
  const handleUpgradeClick = async () => {
    if (isEnterprise) {
      // Enterprise: Show Contact Sales form (no PSP action → skip legal gate)
      setStep(STEPS.ENTERPRISE_CONTACT);
    } else {
      // Standard plans: route through the PSP-neutral legal consent step.
      // The actual Stripe checkout redirect happens ONLY after consent
      // is captured and POST /api/subscriptions/clinics returns 200.
      setStep(STEPS.LEGAL_CONSENT);
    }
  };
  
  /**
   * P3 Phase 3: Submit Enterprise request
   */
  const handleEnterpriseSubmit = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const clinicId = rbacClinicId || clinicUser?.clinicId;
      const clinicEmail = clinicUser?.email;
      const clinicName = clinicUser?.clinicName || 'Your Clinic';
      
      if (!clinicId) {
        setError('Clinic ID not found. Please re-login.');
        setIsLoading(false);
        return;
      }

      await StripeService.requestEnterprisePlan({
        clinicId,
        clinicEmail,
        clinicName,
        message: enterpriseMessage,
      });
      
      setStep(STEPS.ENTERPRISE_SUBMITTED);
      
      toast({
        title: t('upgrade.enterpriseSubmitted', 'Request Submitted'),
        description: t('upgrade.enterpriseSubmittedDesc', 'Our sales team will contact you within 24 hours.'),
      });
      
    } catch (err) {
      console.error('[UpgradeModal] Enterprise request error:', err);
      setError(err.message || 'Failed to submit request. Please try again.');
      
      toast({
        title: t('upgrade.enterpriseError', 'Request Error'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleClose = () => {
    setStep(STEPS.CONFIRM);
    setError(null);
    setEnterpriseMessage('');
    onClose?.();
  };
  
  // Render based on step
  const renderContent = () => {
    switch (step) {
      case STEPS.CONFIRM:
        return (
          <div className="p-6 space-y-5">
            {/* Package Summary */}
            <Card className={`${selectedPackage.color || 'bg-gray-50'} border-2`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${selectedPackage.badgeColor || 'bg-blue-500'} text-white`}>
                    <PackageIcon className="h-7 w-7" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold">{selectedPackage.displayName}</h3>
                    <p className="text-sm opacity-80">{selectedPackage.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{formatPrice(selectedPackage.pricing)}</div>
                    {selectedPackage.pricing?.interval && (
                      <span className="text-sm opacity-70">/{selectedPackage.pricing.interval}</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* What you'll get */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                {t('upgrade.whatYouGet', "What you'll get:")}
              </h4>
              <div className="space-y-2">
                {/* Limits */}
                {selectedPackage.limits?.leads_per_month != null && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>{selectedPackage.limits.leads_per_month === -1
                      ? t('upgrade.unlimitedLeads', 'Unlimited leads')
                      : `${selectedPackage.limits.leads_per_month} leads/month`
                    }</span>
                  </div>
                )}
                {selectedPackage.limits?.sales_reps != null && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>{selectedPackage.limits.sales_reps === -1 ? 'Unlimited' : selectedPackage.limits.sales_reps} included users</span>
                  </div>
                )}
                {/* Dynamic features from backend */}
                {selectedPackage.features && Object.entries(selectedPackage.features).map(([key, value]) => {
                  if (!value) return null;
                  const def = FEATURE_DEFINITIONS[key];
                  if (!def) return null;
                  return (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{def.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Upgrade notice */}
            {currentPackage && currentPackageId !== selectedPackageId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="text-blue-800">
                  {t('upgrade.upgradeFrom', 'Upgrading from {{current}} to {{new}}', {
                    current: currentPackage.displayName,
                    new: selectedPackage.displayName,
                  })}
                </p>
              </div>
            )}
            
            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                {error}
              </div>
            )}
            
            {/* CTA Button */}
            <Button 
              className={`w-full ${isEnterprise ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700'}`}
              size="lg"
              onClick={handleUpgradeClick}
              disabled={isLoading}
              data-testid="upgrade-cta-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('upgrade.processing', 'Processing...')}
                </>
              ) : isEnterprise ? (
                <>
                  <Phone className="h-4 w-4 mr-2" />
                  {t('upgrade.contactSales', 'Contact Sales')}
                </>
              ) : (
                <>
                  {t('upgrade.continueToPayment', 'Continue to Payment')}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
            
            <p className="text-center text-xs text-gray-500">
              <Lock className="h-3 w-3 inline mr-1" />
              {isEnterprise 
                ? t('upgrade.salesTeamContact', 'Our sales team will contact you within 24 hours')
                : t('upgrade.securePayment', 'Secure payment powered by Stripe')
              }
            </p>
            {/* P0-4: TEST MODE indicator */}
            {!isEnterprise && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-2 text-center text-xs font-semibold text-amber-800" data-testid="upgrade-modal-test-mode">
                TEST MODE — No real charges. Stripe is in test/sandbox mode.
              </div>
            )}
          </div>
        );
        
      case STEPS.LEGAL_CONSENT:
        return (
          <SubscriptionLegalGate
            open
            countryContext={clinicCountry}
            submitting={legalSubmitting}
            onCancel={() => { setError(null); setStep(STEPS.CONFIRM); }}
            onAccept={handleLegalAccept}
          />
        );

      case STEPS.ENTERPRISE_CONTACT:
        return (
          <div className="p-6 space-y-5">
            <div className="text-center mb-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <Building2 className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {t('upgrade.enterpriseTitle', 'Enterprise Plan')}
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                {t('upgrade.enterpriseDesc', 'Tell us about your needs and our team will create a custom solution.')}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('upgrade.enterpriseMessage', 'How can we help you?')} (optional)
              </label>
              <Textarea
                placeholder={t('upgrade.enterpriseMessagePlaceholder', 'Tell us about your clinic, number of locations, specific requirements...')}
                value={enterpriseMessage}
                onChange={(e) => setEnterpriseMessage(e.target.value)}
                rows={4}
                data-testid="enterprise-message-input"
              />
            </div>
            
            {/* Contact info preview */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="h-4 w-4" />
                <span>{clinicUser?.email || 'Not available'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Building2 className="h-4 w-4" />
                <span>{clinicUser?.clinicName || 'Your Provider'}</span>
              </div>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                {error}
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setStep(STEPS.CONFIRM)}
                disabled={isLoading}
              >
                {t('common.back', 'Back')}
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={handleEnterpriseSubmit}
                disabled={isLoading}
                data-testid="submit-enterprise-request"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    {t('upgrade.submitRequest', 'Submit Request')}
                  </>
                )}
              </Button>
            </div>
          </div>
        );
        
      case STEPS.REDIRECTING:
        return (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('upgrade.redirecting', 'Redirecting to secure checkout…')}
            </h3>
            <p className="text-gray-600">
              {t('upgrade.redirectingDesc', "You'll be redirected to complete your payment securely.")}
            </p>
          </div>
        );
        
      case STEPS.PSP_FORM:
        return (
          <IyzicoCheckoutForm
            formContent={pspFormContent && pspFormContent.content}
            tokenExpiresIn={pspFormContent && pspFormContent.expiresIn}
            provider={pspFormContent && pspFormContent.provider}
            onCancel={() => {
              setPspFormContent(null);
              setError(null);
              setStep(STEPS.CONFIRM);
            }}
          />
        );

      case STEPS.ENTERPRISE_SUBMITTED:
        return (
          <div className="p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {t('upgrade.enterpriseSuccess', 'Request Submitted!')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('upgrade.enterpriseSuccessDesc', 'Our sales team will contact you within 24 hours to discuss your Enterprise plan.')}
            </p>
            <Badge className="bg-purple-100 text-purple-700 px-4 py-1">
              {t('upgrade.pendingContact', 'Pending Contact')}
            </Badge>
            <Button
              className="w-full mt-6"
              variant="outline"
              onClick={handleClose}
            >
              {t('common.close', 'Close')}
            </Button>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        {step !== STEPS.ENTERPRISE_SUBMITTED && step !== STEPS.REDIRECTING && (
          <div className={`px-6 py-4 ${isEnterprise ? 'bg-purple-600' : selectedPackage.badgeColor || 'bg-blue-600'} text-white`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">
                {step === STEPS.CONFIRM
                  ? t('upgrade.confirmTitle', 'Confirm Upgrade')
                  : step === STEPS.LEGAL_CONSENT
                    ? t('upgrade.legalTitle', 'Legal Agreements')
                    : t('upgrade.enterpriseContact', 'Contact Sales')
                }
              </h2>
              <button 
                onClick={handleClose}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="close-upgrade-modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
        
        {renderContent()}
      </div>
    </div>
  );
};

export default UpgradeModal;
